"use client";

import { useEffect, useState } from "react";

type Props = { error: Error & { digest?: string }; retry: () => void };

// Replaces the root layout when even that fails, so it cannot rely on the
// global stylesheet, fonts or any provider.
export default function GlobalError({ error, retry }: Props) {
  const [time] = useState(() => new Date());

  useEffect(() => {
    console.error(error);
  }, [error]);

  const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 14, wordBreak: "break-all" } as const;

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: "#684f3f",
          color: "#ffffff",
          fontFamily: "Georgia, 'Times New Roman', serif",
        }}
      >
        <title>Something went wrong | The Dear Jane</title>
        <main style={{ maxWidth: 560 }}>
          <p style={{ fontStyle: "italic", color: "#ffffff", fontSize: 20, margin: 0 }}>The Dear Jane</p>
          <h1 style={{ fontSize: 44, lineHeight: 1.1, fontWeight: 400, margin: "8px 0 0" }}>The shop didn&apos;t load</h1>
          <p style={{ fontSize: 18, lineHeight: 1.6 }}>
            Something failed before the page could be put together. Trying again usually fixes it. Your cart is stored
            separately and hasn&apos;t been touched.
          </p>
          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap", marginTop: 24 }}>
            <button
              type="button"
              onClick={() => retry()}
              style={{
                background: "#f0d08a",
                color: "#231a15",
                border: 0,
                borderRadius: 4,
                padding: "14px 28px",
                fontSize: 15,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            {/* A full reload on purpose: client-side routing may be what broke. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/" style={{ color: "#ffffff", textDecorationColor: "#f0d08a", textUnderlineOffset: 4 }}>
              Go to the homepage
            </a>
          </div>
          <dl
            style={{
              marginTop: 40,
              padding: 20,
              background: "#684f3f",
              borderRadius: 4,
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "6px 24px",
              fontFamily: "system-ui, sans-serif",
              fontSize: 15,
            }}
          >
            <dt style={{ color: "#ffffff" }}>Time</dt>
            <dd style={{ margin: 0, ...mono }}>{time.toISOString()}</dd>
            <dt style={{ color: "#ffffff" }}>Reference</dt>
            <dd style={{ margin: 0, ...mono }}>{error.digest ?? "none"}</dd>
            {process.env.NODE_ENV === "development" ? (
              <>
                <dt style={{ color: "#ffffff" }}>Error</dt>
                <dd style={{ margin: 0, ...mono }}>
                  {error.name}: {error.message}
                </dd>
              </>
            ) : null}
          </dl>
          <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 14, color: "#ffffff" }}>
            If this keeps happening, send us the time and reference above so we can find the cause.
          </p>
        </main>
      </body>
    </html>
  );
}
